import { FC, MouseEvent, useState, useMemo, useCallback, useEffect, useRef } from "react";
import {
    PersonalizedTagWrapper,
    Title,
    SubmitButton,
    PersonalizedHeader,
    PointResultContainer,
    PersonalizedTagModalWrapper,
    ContentContainer,
    ImageBlock,
    ImageBlockPlaceholder,
    ImagePlaceholderTitle,
    ImagePlaceholderFormat,
    ImageFileInput,
    FieldsColumn,
    ImageLabel,
    ImageDeleteButton,
} from "./style";
import { BrgButton, ButtonVariant, CommonInput, FlexBox, INPUT_TYPES, useToasts } from "@brg/ui";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { TagAdminSelector } from "shared/reducers/tagAdminSlice";
import { API, Designer, DesignerAPI, Endpoints, Tag, TagsAPI } from "@brg/api";
import { TagsActions } from "shared/reducers/Tags";
import ResItem from "_geograffee/places/pages/tagPlaces/components/map/resultitem";
import { useDebouncedEffect } from "shared/components/DropdownSearchBar/DropdownSearchBar";
import ProjectAuthorsEdit from "shared/pages/projectEdit/ProjectAuthorsEdit";
import { AppTester } from "supporting";
import { getTagsModalLocalNameLabelKey } from "shared/helpers/tagAdminLocalLang";
import { useSimpleUploader } from "@brg/uploader";
import { Voidable } from "@brg/toolbox";
import { MAX_FILE_SIZE } from "shared/pages/projectEdit/constants";

const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/tiff', 'image/tif'];

const buildTagImagePayload = (fileId: string, localization: string) => ({
    imageContainerId: fileId,
    images: [{
        fileId,
        containerId: fileId,
        isNew: true,
        isDeleted: false,
        inProgress: false,
        localization,
    }],
});

export const CreatePersonalizedTagComponent: FC<{ 
    onCreateTag?: (value?: any) => void, 
    onClose: () => void, 
    isModal?: boolean,
    isEducation?: boolean,
    isPersonalized?: boolean,
    editTag?: Tag<'Global'>,
    onUpdateTag?: (value?: any) => void
}> = ({ 
        onClose, 
        isModal,
        isEducation = false,
        onCreateTag,
        editTag,
        onUpdateTag
    }) => {
    const { t, i18n } = useTranslation();
    const dispatch = useDispatch();
    const {info, error } = useToasts();
    const isDafes = AppTester.isGeoDafes() || AppTester?.isApp('kz')
    const { currentPlatform } = useSelector(TagAdminSelector)
    const [pointResults, setPoinResults] = useState<any[] | null>([])
    const [imageFileId, setImageFileId] = useState<string | null>(null)
    const [existingImageUrl, setExistingImageUrl] = useState<string | null>(null)
    const [imageCleared, setImageCleared] = useState(false)
    const sipRef = useRef<any>(null)
    const [data, setData] = useState({
        nameEng: '',
        nameRu: '',
        user: '',
        coordinate: '',
        author: null,
        description: '',
    })

    const isEditMode = useMemo(() => !!editTag, [editTag])

    const disable = useMemo(() => (data?.nameEng === '' || data?.nameRu === '') , [data])

    const parent = useMemo(
        () => currentPlatform?.editCategory?.mainTag || undefined , [currentPlatform])

    const uploadValidator = useCallback(async (files: any) => {
        const file = files?.[0];
        if (!file) return false;

        if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
            error(t('tags_modal.tag_image_wrong_format'));
            if (sipRef.current?.uploader?.inputRef?.current) {
                sipRef.current.uploader.inputRef.current.value = '';
            }
            return false;
        }

        if (file.size > MAX_FILE_SIZE) {
            error(t('project_edit.image_error.max_size'));
            if (sipRef.current?.uploader?.inputRef?.current) {
                sipRef.current.uploader.inputRef.current.value = '';
            }
            return false;
        }

        return true;
    }, [error, t]);

    const uploaderCallback = useCallback((fileId: Voidable<string>) => {
        if (fileId) {
            setImageFileId(fileId);
            setImageCleared(false);
        }
    }, []);

    const sip = useSimpleUploader(uploaderCallback, uploadValidator);
    sipRef.current = sip;

    const imagePreviewUrl = useMemo(() => {
        if (imageFileId && sip.imgSrc) {
            return sip.imgSrc;
        }
        return existingImageUrl;
    }, [imageFileId, sip.imgSrc, existingImageUrl]);

    const onRemoveImage = (e: MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setImageFileId(null);
        setExistingImageUrl(null);
        setImageCleared(true);
        sip.imgSrc = null;
        sipRef.current?.uploader?.uploader?.uppy?.reset();
        if (sipRef.current?.uploader?.inputRef?.current) {
            sipRef.current.uploader.inputRef.current.value = '';
        }
    };

    useEffect(() => {
        if (!isEditMode || !editTag) return;

        let cancelled = false;

        const nameEng = editTag.name?.find((n: any) => n.lang?.toLowerCase() === 'en')?.name || '';
        const nameRu = editTag.name?.find((n: any) => n.lang?.toLowerCase() === 'ru')?.name || '';
        const description = (editTag as any)?.description?.find((n: any) => n.lang?.toLowerCase() === 'ru')?.name
            || (editTag as any)?.description?.find((n: any) => n.lang?.toLowerCase() === 'en')?.name
            || (editTag as any)?.description?.[0]?.name
            || '';

        setData({
            nameEng,
            nameRu,
            user: '',
            coordinate: editTag.point ? `${editTag.point.latitude} ${editTag.point.longitude}` : '',
            author: null,
            description,
        });

        const existingImage = (editTag as any)?.images?.find((img: any) => img.size === 'original')
            || (editTag as any)?.images?.[0];
        if (existingImage?.url) {
            setExistingImageUrl(`${Endpoints.files}/${existingImage.url}`);
        } else {
            setExistingImageUrl(null);
        }
        setImageFileId(null);
        setImageCleared(false);

        const assignedAccountId = (editTag as any)?.personalzedAccountId || (editTag as any)?.personalizedAccountId;
        if (!assignedAccountId) return;

        DesignerAPI.Get({ idDesigner: assignedAccountId })
            .then((v) => v.json())
            .then((v) => API.toLang<Designer>(v, i18n.language))
            .then((v) => {
                if (cancelled || !v) return;
                setData((prev) => ({ ...prev, author: [v] as any }));
            })
            .catch(() => {});

        return () => {
            cancelled = true;
        };
    }, [isEditMode, editTag, i18n.language])

    const onClickRes = (coor: string) => {
        setData({...data, ['coordinate']: coor })
        setPoinResults(null)
    }

    // @ts-ignore
    const onProjectAuthorsChange = useCallback(({ authors }) => {
        if (authors.length > 1) {
            error('Автор может быть только один!');
            return;
        }

        if (!authors.length) {
            setData((prev) => ({ ...prev, author: null }));
            return;
        }

        const [author] = authors;
        DesignerAPI.Get(({
            idDesigner: author.accountId
        }))
        .then((v) => v.json())
        .then((v) => API.toLang<Designer>(v, i18n.language))
        .then(
            (v) => {
                //@ts-ignore
                setData((prev) => ({ ...prev, author: [v] }));
        })
    }, [error, i18n]);

    const onSearchPoint = () => {
        fetch(`https://geocode-maps.yandex.ru/1.x/?apikey=44fd3270-0c0b-444d-a600-6f3b5e5a6343&geocode=${data?.coordinate.split(" ").reverse().join(" ")}&format=json`)
        .then((response) => {
            return response.json();
        })
        .then((data) => {
            if (data.error) return;

            const coll = data.response.GeoObjectCollection.featureMember;

            if (coll.length > 0) {
                //@ts-ignore
                const collSorted = coll.map((item, i) => {
                    return  <ResItem
                                key={i}
                                item={item}
                                className={'point-item'}
                                isNoResult={false}
                                name={item.GeoObject.name}
                                description={item.GeoObject.description}
                                coordinates={item.GeoObject.Point.pos}
                                styles={{
                                    background: '#f2f2f2',
                                    color: '#151514 !important',
                                }}
                                onClickRes={() => {
                                    onClickRes(item.GeoObject.Point.pos.split(" ").reverse().join(" "))
                                }}
                            />
                })
                setPoinResults(collSorted);
                return;
            }
            setPoinResults(null)
            return;
        });
    }

    useDebouncedEffect(() => {

        if (data?.coordinate.match(/^\d+(?:[,.]\d+)?(?:,.\d+(?:[,.]\d+)?)*$/)) {
            setData({...data, ['coordinate']: data?.coordinate.replace(", ", " ")})
        }

        if (data?.coordinate.match(/^\d+(?:[,.]\d+)?(?: \d+(?:[,.]\d+)?)*$/)) {
            //out of range check
            let latitude = data?.coordinate.split(" ")[0]
            let longitude = data?.coordinate.split(" ")[1]
            //@ts-ignore
            if (Math.abs(latitude) > 90) {
                //@ts-ignore
                if (Math.abs(longitude) > 90 || Math.abs(latitude) > 180) return
                setData({...data, ['coordinate']: [longitude, latitude].join(" ")})
                return
            }

            //@ts-ignore
            if (Math.abs(longitude) > 180) return

            setData({...data, ['coordinate']: data?.coordinate})
        }

        if (data?.coordinate.length > 3 && !data?.coordinate.match(/^\d+(?:[,.]\d+)?(?: \d+(?:[,.]\d+)?)*$/)) {
            onSearchPoint();
        }

        if (data?.coordinate.length === 0) {
            return
        }

    }, [data?.coordinate], 1000);

    const onUpdateUserTags = async (newTagId: string) => {
        //@ts-ignore
        if (!data?.author?.length) return null;
        const designer = (data?.author?.[0] as unknown) as Designer<'Local'>;

        return  await API.request()
        .endpoint(Endpoints.geograffee)
        .url('/api/Designer/UpdateTagList')
        .post()
        .addQuery('tagsIds', [...designer?.tagListId, newTagId])
        .addQuery('designerId', designer?.designerId)
        .send()
        .then((res) => {
          if (res.status === 200) {
            info('Теги пользователя обновлены');
          } else {
            error('Ошибка запроса');
          }
        })
    }

    const onSubmit = () => {
        if (!isEditMode && !parent) {
            return;
        }

        if (data?.nameRu.trim().length < 2 || data?.nameEng.trim().length < 2) {
            error(t('main_admin_tag.error.min_length')); 
            return;
        }

        const imagePayload = imageFileId
            ? buildTagImagePayload(imageFileId, i18n.language)
            : null;

        //@ts-ignore
        const selectedAuthor = data?.author?.[0] as Designer<'Local'> | undefined;
        const personalizedAccountId = selectedAuthor?.accountId || selectedAuthor?.designerId;
        const personalizedPayload = {
            isPersonalized: true,
            ...(personalizedAccountId ? { personalzedAccountId: personalizedAccountId } : {}),
        };

        const descriptionPayload = data?.description?.trim()
            ? {
                description: [
                    { lang: 'Ru', name: data.description },
                    { lang: 'En', name: data.description },
                ],
            }
            : null;

        if (isEditMode && editTag) {
            const updatedTag = {
                ...editTag,
                name: [
                  { lang: 'En', name: data?.nameEng },
                  { lang: 'Ru', name: data?.nameRu },
                ],
                ...(!!data?.coordinate?.length && {
                    point: {
                        name: [
                          { lang: 'En', name: data?.nameEng },
                          { lang: 'Ru', name: data?.nameRu },
                        ],
                        latitude: parseFloat(data?.coordinate.split(" ")[0]),
                        longitude: parseFloat(data?.coordinate.split(" ")[1]),
                      },
                }),
                ...(imagePayload || (imageCleared ? { images: [], imageContainerId: null } : {})),
                ...personalizedPayload,
                ...(descriptionPayload || {}),
            };

            TagsAPI.UpdateTag(updatedTag)
                .then((res) => {
                    if (res?.status === 200) {
                        onUpdateTag && onUpdateTag(updatedTag);
                        onClose && onClose();
                    } else if (res?.status === 400) {
                        error(t('tags_modal.update_tag_name_exists'));
                    } else {
                        error(t('tags_modal.update_tag_error'));
                    }
                })
                .catch((err: any) => {
                    error(t('tags_modal.update_tag_error'));
                });
            return;
        }

        const newTagProps = {
            name: [
              { lang: 'En', name: data?.nameEng },
              { lang: 'Ru', name: data?.nameRu },
            ],
            groupId: parent?.tagId,
            groupIds: [parent?.tagId],
            entityTypes: parent.entityTypes,
            level: 0,
            isEducationTag: isEducation,
            point: null,
            imageContainerId: null,
            ...(imagePayload?.images ? { images: imagePayload.images } : {}),
            ...personalizedPayload,
            ...(descriptionPayload || {}),
        }

        API.request()
        .endpoint(Endpoints.tagsMnePw)
        .url('/api/TagAdmin/AddTags')
        .post()
        .body([newTagProps])
        .send()
        .then(res => {
            if (res) {
                const translatedTag = API.toLang(res, i18n.language)
                onUpdateUserTags(res?.tagId)
                dispatch(TagsActions.setNewTag(translatedTag))
                onClose && onClose()

                if (isModal && !!onCreateTag) {
                    onCreateTag && onCreateTag(res)
                } else {
                    info(t('add_new_tag_success'))
                    onCreateTag && onCreateTag();
                }
            }
        }).catch((err: any) => {
            error(t('add_new_tag_error'))
        })
    }

    const onInputChange = (ev: any, field: string) => {
        const value = ev.target.value;

        if (field === 'description') {
            setData(prev => ({
              ...prev,
              description: value
            }));
            return;
        }

        const baseRegex = /^[a-zA-Zа-яА-ЯёЁ0-9\s!"№%&,./:;?()+'\-"\u00C0-\u024F]*$/;
        const extendedRegex = /^[a-zA-Zа-яА-ЯёЁ0-9\s!"№%&,./:;?()+'\-"\u00C0-\u024F\u4e00-\u9fff]*$/;

        const regex = isDafes ? extendedRegex : baseRegex;
      
        if (!regex.test(value)) {
          return;
        }
      
        setData(prev => ({
          ...prev,
          [field]: value
        }));
    }

    const formContent = (
        <ContentContainer>
            <ImageBlock direction="column">
                <ImageLabel>{t('tags_modal.tag_image')}</ImageLabel>
                <ImageBlockPlaceholder>
                    {imagePreviewUrl ? (
                        <img src={imagePreviewUrl} alt="" />
                    ) : (
                        <>
                            <ImagePlaceholderTitle>
                                {t('tags_modal.tag_image_placeholder')}
                            </ImagePlaceholderTitle>
                            <ImagePlaceholderFormat>
                                {t('tags_modal.tag_image_format')}
                            </ImagePlaceholderFormat>
                        </>
                    )}
                    <ImageFileInput
                        {...sip.inputProps}
                        accept={ALLOWED_IMAGE_TYPES.join(',')}
                    />
                    {!!imagePreviewUrl && (
                        <ImageDeleteButton type="button" onClick={onRemoveImage}>
                            <svg width="40" height="32" viewBox="0 0 40 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path
                                    fillRule="evenodd"
                                    clipRule="evenodd"
                                    d="M17.4268 5.96289C16.5983 5.96289 15.9268 6.63446 15.9268 7.46289V7.96289H12.4268C11.5983 7.96289 10.9268 8.63446 10.9268 9.46289V10.4629C10.9268 11.2913 11.5983 11.9629 12.4268 11.9629H12.9268V24.4629C12.9268 25.2913 13.5983 25.9629 14.4268 25.9629H25.4268C26.2552 25.9629 26.9268 25.2913 26.9268 24.4629V11.9629H27.4268C28.2552 11.9629 28.9268 11.2913 28.9268 10.4629V9.46289C28.9268 8.63446 28.2552 7.96289 27.4268 7.96289H23.9268V7.46289C23.9268 6.63446 23.2552 5.96289 22.4268 5.96289H17.4268ZM22.9268 7.96289V7.46289C22.9268 7.18675 22.7029 6.96289 22.4268 6.96289H17.4268C17.1506 6.96289 16.9268 7.18675 16.9268 7.46289V7.96289H22.9268ZM15.9268 8.96289H12.4268C12.1506 8.96289 11.9268 9.18675 11.9268 9.46289V10.4629C11.9268 10.739 12.1506 10.9629 12.4268 10.9629H12.9268H13.4268H26.4268H26.9268H27.4268C27.7029 10.9629 27.9268 10.739 27.9268 10.4629V9.46289C27.9268 9.18675 27.7029 8.96289 27.4268 8.96289H23.9268H23.4268H16.4268H15.9268ZM13.9268 11.9629H25.9268V24.4629C25.9268 24.739 25.7029 24.9629 25.4268 24.9629H14.4268C14.1506 24.9629 13.9268 24.739 13.9268 24.4629V11.9629Z"
                                    fill="#E7E7E7"
                                />
                            </svg>
                        </ImageDeleteButton>
                    )}
                </ImageBlockPlaceholder>
            </ImageBlock>

            <FieldsColumn>
                <FlexBox style={{ position: "relative" }} direction="column" width="100%">
                    <CommonInput
                        type={INPUT_TYPES.text}
                        value={data?.nameEng}
                        onChange={(ev: any) => onInputChange(ev, 'nameEng')}
                        orientation="column"
                        label={t("tags_modal.tag_name_eng")}
                        placeholder={t("tags_modal.tag_input_placeholder")}
                    />
                </FlexBox>

                <FlexBox style={{ position: "relative" }} direction="column" width="100%">
                    <CommonInput
                        type={INPUT_TYPES.text}
                        value={data?.nameRu}
                        onChange={(ev: any) => onInputChange(ev, 'nameRu')}
                        orientation="column"
                        label={t(getTagsModalLocalNameLabelKey())}
                        placeholder={t("tags_modal.tag_input_placeholder")}
                    />
                </FlexBox>

                {/* <FlexBox style={{ position: 'relative' }} direction="column" width="100%">
                    <CommonInput
                        type={INPUT_TYPES.search}
                        value={data?.coordinate}
                        onChange={(ev: any) => onInputChange(ev, 'coordinate')}
                        orientation="column"
                        label={t("tags_modal.tag_geo_places")}
                        placeholder={t("tags_modal.geo_places_search")}
                        infoText={t('tags_modal.for_tag_point')}
                        onWhiteBG
                    />
                    {!!pointResults?.length && (
                        <PointResultContainer>
                            {pointResults}
                        </PointResultContainer>
                    )}
                </FlexBox> */}

                <FlexBox direction="column" width="100%">
                    <ProjectAuthorsEdit
                        label={t("tags_modal.tag_user")}
                        infoText={t('tags_modal.for_tag_artists')}
                        value={data?.author || []}
                        onChange={onProjectAuthorsChange}
                        orientation="column"
                        persist
                    />
                </FlexBox>

                <FlexBox direction="column" width="100%">
                    <CommonInput
                        type={INPUT_TYPES.textarea}
                        value={data?.description}
                        onChange={(ev: any) => onInputChange(ev, 'description')}
                        orientation="column"
                        label={t('tags_modal.description_label')}
                        placeholder={t('tags_modal.description_placeholder')}
                        onWhiteBG
                    />
                </FlexBox>
            </FieldsColumn>
        </ContentContainer>
    );

    const header = (
        <PersonalizedHeader>
            <Title>{isEditMode ? t("tags_modal.edit_tag_title") : t("tags_modal.create_tag_title")}</Title>
            {isModal ? (
                <BrgButton
                    variant={ButtonVariant.Filled}
                    onClick={onSubmit}
                    disabled={disable}
                >
                    {t("tags_modal.save_title")}
                </BrgButton>
            ) : (
                <SubmitButton 
                    onClick={onSubmit} 
                    disabled={disable}
                >
                    {t("tags_modal.save_title")}
                </SubmitButton>
            )}
        </PersonalizedHeader>
    );

    if (isModal) {
        return (
           <PersonalizedTagModalWrapper>
            {header}
            {formContent}
           </PersonalizedTagModalWrapper>
        )
    }

    return (
        <PersonalizedTagWrapper>
            {header}
            {formContent}
        </PersonalizedTagWrapper>
    )
}
import { FC, MouseEvent, useState, useMemo, useCallback, useEffect, useRef } from "react";
import {
    PersonalizedTagWrapper,
    Title,
    SubmitButton,
    PersonalizedHeader,
    PointResultContainer,
    PersonalizedTagModalWrapper,
    ContentContainer,
    ImageBlock,
    ImageBlockPlaceholder,
    ImagePlaceholderTitle,
    ImagePlaceholderFormat,
    ImageFileInput,
    FieldsColumn,
    ImageLabel,
    ImageDeleteButton,
} from "./style";
import { BrgButton, ButtonVariant, CommonInput, FlexBox, INPUT_TYPES, useToasts } from "@brg/ui";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { TagAdminSelector } from "shared/reducers/tagAdminSlice";
import { API, Designer, DesignerAPI, Endpoints, Tag, TagsAPI } from "@brg/api";
import { TagsActions } from "shared/reducers/Tags";
import ResItem from "_geograffee/places/pages/tagPlaces/components/map/resultitem";
import { useDebouncedEffect } from "shared/components/DropdownSearchBar/DropdownSearchBar";
import ProjectAuthorsEdit from "shared/pages/projectEdit/ProjectAuthorsEdit";
import { AppTester } from "supporting";
import { getTagsModalLocalNameLabelKey } from "shared/helpers/tagAdminLocalLang";
import { useSimpleUploader } from "@brg/uploader";
import { Voidable } from "@brg/toolbox";
import { MAX_FILE_SIZE } from "shared/pages/projectEdit/constants";

const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/tiff', 'image/tif'];

const buildTagImagePayload = (fileId: string, localization: string) => ({
    imageContainerId: fileId,
    images: [{
        fileId,
        containerId: fileId,
        isNew: true,
        isDeleted: false,
        inProgress: false,
        localization,
    }],
});

export const CreatePersonalizedTagComponent: FC<{ 
    onCreateTag?: (value?: any) => void, 
    onClose: () => void, 
    isModal?: boolean,
    isEducation?: boolean,
    isPersonalized?: boolean,
    editTag?: Tag<'Global'>,
    onUpdateTag?: (value?: any) => void
}> = ({ 
        onClose, 
        isModal,
        isEducation = false,
        onCreateTag,
        editTag,
        onUpdateTag
    }) => {
    const { t, i18n } = useTranslation();
    const dispatch = useDispatch();
    const {info, error } = useToasts();
    const isDafes = AppTester.isGeoDafes() || AppTester?.isApp('kz')
    const { currentPlatform } = useSelector(TagAdminSelector)
    const [pointResults, setPoinResults] = useState<any[] | null>([])
    const [imageFileId, setImageFileId] = useState<string | null>(null)
    const [existingImageUrl, setExistingImageUrl] = useState<string | null>(null)
    const [imageCleared, setImageCleared] = useState(false)
    const sipRef = useRef<any>(null)
    const [data, setData] = useState({
        nameEng: '',
        nameRu: '',
        user: '',
        coordinate: '',
        author: null,
        description: '',
    })

    const isEditMode = useMemo(() => !!editTag, [editTag])

    const disable = useMemo(() => (data?.nameEng === '' || data?.nameRu === '') , [data])

    const parent = useMemo(
        () => currentPlatform?.editCategory?.mainTag || undefined , [currentPlatform])

    const uploadValidator = useCallback(async (files: any) => {
        const file = files?.[0];
        if (!file) return false;

        if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
            error(t('tags_modal.tag_image_wrong_format'));
            if (sipRef.current?.uploader?.inputRef?.current) {
                sipRef.current.uploader.inputRef.current.value = '';
            }
            return false;
        }

        if (file.size > MAX_FILE_SIZE) {
            error(t('project_edit.image_error.max_size'));
            if (sipRef.current?.uploader?.inputRef?.current) {
                sipRef.current.uploader.inputRef.current.value = '';
            }
            return false;
        }

        return true;
    }, [error, t]);

    const uploaderCallback = useCallback((fileId: Voidable<string>) => {
        if (fileId) {
            setImageFileId(fileId);
            setImageCleared(false);
        }
    }, []);

    const sip = useSimpleUploader(uploaderCallback, uploadValidator);
    sipRef.current = sip;

    const imagePreviewUrl = useMemo(() => {
        if (imageFileId && sip.imgSrc) {
            return sip.imgSrc;
        }
        return existingImageUrl;
    }, [imageFileId, sip.imgSrc, existingImageUrl]);

    const onRemoveImage = (e: MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setImageFileId(null);
        setExistingImageUrl(null);
        setImageCleared(true);
        sip.imgSrc = null;
        sipRef.current?.uploader?.uploader?.uppy?.reset();
        if (sipRef.current?.uploader?.inputRef?.current) {
            sipRef.current.uploader.inputRef.current.value = '';
        }
    };

    useEffect(() => {
        if (!isEditMode || !editTag) return;

        let cancelled = false;

        const nameEng = editTag.name?.find((n: any) => n.lang?.toLowerCase() === 'en')?.name || '';
        const nameRu = editTag.name?.find((n: any) => n.lang?.toLowerCase() === 'ru')?.name || '';
        const description = (editTag as any)?.description?.find((n: any) => n.lang?.toLowerCase() === 'ru')?.name
            || (editTag as any)?.description?.find((n: any) => n.lang?.toLowerCase() === 'en')?.name
            || (editTag as any)?.description?.[0]?.name
            || '';

        setData({
            nameEng,
            nameRu,
            user: '',
            coordinate: editTag.point ? `${editTag.point.latitude} ${editTag.point.longitude}` : '',
            author: null,
            description,
        });

        const existingImage = (editTag as any)?.images?.find((img: any) => img.size === 'original')
            || (editTag as any)?.images?.[0];
        if (existingImage?.url) {
            setExistingImageUrl(`${Endpoints.files}/${existingImage.url}`);
        } else {
            setExistingImageUrl(null);
        }
        setImageFileId(null);
        setImageCleared(false);

        const assignedAccountId = (editTag as any)?.personalzedAccountId || (editTag as any)?.personalizedAccountId;
        if (!assignedAccountId) return;

        DesignerAPI.Get({ idDesigner: assignedAccountId })
            .then((v) => v.json())
            .then((v) => API.toLang<Designer>(v, i18n.language))
            .then((v) => {
                if (cancelled || !v) return;
                setData((prev) => ({ ...prev, author: [v] as any }));
            })
            .catch(() => {});

        return () => {
            cancelled = true;
        };
    }, [isEditMode, editTag, i18n.language])

    const onClickRes = (coor: string) => {
        setData({...data, ['coordinate']: coor })
        setPoinResults(null)
    }

    // @ts-ignore
    const onProjectAuthorsChange = useCallback(({ authors }) => {
        if (authors.length > 1) {
            error('Автор может быть только один!');
            return;
        }

        if (!authors.length) {
            setData((prev) => ({ ...prev, author: null }));
            return;
        }

        const [author] = authors;
        DesignerAPI.Get(({
            idDesigner: author.accountId
        }))
        .then((v) => v.json())
        .then((v) => API.toLang<Designer>(v, i18n.language))
        .then(
            (v) => {
                //@ts-ignore
                setData((prev) => ({ ...prev, author: [v] }));
        })
    }, [error, i18n]);

    const onSearchPoint = () => {
        fetch(`https://geocode-maps.yandex.ru/1.x/?apikey=44fd3270-0c0b-444d-a600-6f3b5e5a6343&geocode=${data?.coordinate.split(" ").reverse().join(" ")}&format=json`)
        .then((response) => {
            return response.json();
        })
        .then((data) => {
            if (data.error) return;

            const coll = data.response.GeoObjectCollection.featureMember;

            if (coll.length > 0) {
                //@ts-ignore
                const collSorted = coll.map((item, i) => {
                    return  <ResItem
                                key={i}
                                item={item}
                                className={'point-item'}
                                isNoResult={false}
                                name={item.GeoObject.name}
                                description={item.GeoObject.description}
                                coordinates={item.GeoObject.Point.pos}
                                styles={{
                                    background: '#f2f2f2',
                                    color: '#151514 !important',
                                }}
                                onClickRes={() => {
                                    onClickRes(item.GeoObject.Point.pos.split(" ").reverse().join(" "))
                                }}
                            />
                })
                setPoinResults(collSorted);
                return;
            }
            setPoinResults(null)
            return;
        });
    }

    useDebouncedEffect(() => {

        if (data?.coordinate.match(/^\d+(?:[,.]\d+)?(?:,.\d+(?:[,.]\d+)?)*$/)) {
            setData({...data, ['coordinate']: data?.coordinate.replace(", ", " ")})
        }

        if (data?.coordinate.match(/^\d+(?:[,.]\d+)?(?: \d+(?:[,.]\d+)?)*$/)) {
            //out of range check
            let latitude = data?.coordinate.split(" ")[0]
            let longitude = data?.coordinate.split(" ")[1]
            //@ts-ignore
            if (Math.abs(latitude) > 90) {
                //@ts-ignore
                if (Math.abs(longitude) > 90 || Math.abs(latitude) > 180) return
                setData({...data, ['coordinate']: [longitude, latitude].join(" ")})
                return
            }

            //@ts-ignore
            if (Math.abs(longitude) > 180) return

            setData({...data, ['coordinate']: data?.coordinate})
        }

        if (data?.coordinate.length > 3 && !data?.coordinate.match(/^\d+(?:[,.]\d+)?(?: \d+(?:[,.]\d+)?)*$/)) {
            onSearchPoint();
        }

        if (data?.coordinate.length === 0) {
            return
        }

    }, [data?.coordinate], 1000);

    const onUpdateUserTags = async (newTagId: string) => {
        //@ts-ignore
        if (!data?.author?.length) return null;
        const designer = (data?.author?.[0] as unknown) as Designer<'Local'>;

        return  await API.request()
        .endpoint(Endpoints.geograffee)
        .url('/api/Designer/UpdateTagList')
        .post()
        .addQuery('tagsIds', [...designer?.tagListId, newTagId])
        .addQuery('designerId', designer?.designerId)
        .send()
        .then((res) => {
          if (res.status === 200) {
            info('Теги пользователя обновлены');
          } else {
            error('Ошибка запроса');
          }
        })
    }

    const onSubmit = () => {
        if (!isEditMode && !parent) {
            return;
        }

        if (data?.nameRu.trim().length < 2 || data?.nameEng.trim().length < 2) {
            error(t('main_admin_tag.error.min_length')); 
            return;
        }

        const imagePayload = imageFileId
            ? buildTagImagePayload(imageFileId, i18n.language)
            : null;

        //@ts-ignore
        const selectedAuthor = data?.author?.[0] as Designer<'Local'> | undefined;
        const personalizedAccountId = selectedAuthor?.accountId || selectedAuthor?.designerId;
        const personalizedPayload = {
            isPersonalized: true,
            ...(personalizedAccountId ? { personalzedAccountId: personalizedAccountId } : {}),
        };

        const descriptionPayload = data?.description?.trim()
            ? {
                description: [
                    { lang: 'Ru', name: data.description },
                    { lang: 'En', name: data.description },
                ],
            }
            : null;

        if (isEditMode && editTag) {
            const updatedTag = {
                ...editTag,
                name: [
                  { lang: 'En', name: data?.nameEng },
                  { lang: 'Ru', name: data?.nameRu },
                ],
                ...(!!data?.coordinate?.length && {
                    point: {
                        name: [
                          { lang: 'En', name: data?.nameEng },
                          { lang: 'Ru', name: data?.nameRu },
                        ],
                        latitude: parseFloat(data?.coordinate.split(" ")[0]),
                        longitude: parseFloat(data?.coordinate.split(" ")[1]),
                      },
                }),
                ...(imagePayload || (imageCleared ? { images: [], imageContainerId: null } : {})),
                ...personalizedPayload,
                ...(descriptionPayload || {}),
            };

            TagsAPI.UpdateTag(updatedTag)
                .then((res) => {
                    if (res?.status === 200) {
                        onUpdateTag && onUpdateTag(updatedTag);
                        onClose && onClose();
                    } else if (res?.status === 400) {
                        error(t('tags_modal.update_tag_name_exists'));
                    } else {
                        error(t('tags_modal.update_tag_error'));
                    }
                })
                .catch((err: any) => {
                    error(t('tags_modal.update_tag_error'));
                });
            return;
        }

        const newTagProps = {
            name: [
              { lang: 'En', name: data?.nameEng },
              { lang: 'Ru', name: data?.nameRu },
            ],
            groupId: parent?.tagId,
            groupIds: [parent?.tagId],
            entityTypes: parent.entityTypes,
            level: 0,
            isEducationTag: isEducation,
            point: null,
            imageContainerId: null,
            ...(imagePayload?.images ? { images: imagePayload.images } : {}),
            ...personalizedPayload,
            ...(descriptionPayload || {}),
        }

        API.request()
        .endpoint(Endpoints.tagsMnePw)
        .url('/api/TagAdmin/AddTags')
        .post()
        .body([newTagProps])
        .send()
        .then(res => {
            if (res) {
                const translatedTag = API.toLang(res, i18n.language)
                onUpdateUserTags(res?.tagId)
                dispatch(TagsActions.setNewTag(translatedTag))
                onClose && onClose()

                if (isModal && !!onCreateTag) {
                    onCreateTag && onCreateTag(res)
                } else {
                    info(t('add_new_tag_success'))
                    onCreateTag && onCreateTag();
                }
            }
        }).catch((err: any) => {
            error(t('add_new_tag_error'))
        })
    }

    const onInputChange = (ev: any, field: string) => {
        const value = ev.target.value;

        if (field === 'description') {
            setData(prev => ({
              ...prev,
              description: value
            }));
            return;
        }

        const baseRegex = /^[a-zA-Zа-яА-ЯёЁ0-9\s!"№%&,./:;?()+'\-"\u00C0-\u024F]*$/;
        const extendedRegex = /^[a-zA-Zа-яА-ЯёЁ0-9\s!"№%&,./:;?()+'\-"\u00C0-\u024F\u4e00-\u9fff]*$/;

        const regex = isDafes ? extendedRegex : baseRegex;
      
        if (!regex.test(value)) {
          return;
        }
      
        setData(prev => ({
          ...prev,
          [field]: value
        }));
    }

    const formContent = (
        <ContentContainer>
            <ImageBlock direction="column">
                <ImageLabel>{t('tags_modal.tag_image')}</ImageLabel>
                <ImageBlockPlaceholder>
                    {imagePreviewUrl ? (
                        <img src={imagePreviewUrl} alt="" />
                    ) : (
                        <>
                            <ImagePlaceholderTitle>
                                {t('tags_modal.tag_image_placeholder')}
                            </ImagePlaceholderTitle>
                            <ImagePlaceholderFormat>
                                {t('tags_modal.tag_image_format')}
                            </ImagePlaceholderFormat>
                        </>
                    )}
                    <ImageFileInput
                        {...sip.inputProps}
                        accept={ALLOWED_IMAGE_TYPES.join(',')}
                    />
                    {!!imagePreviewUrl && (
                        <ImageDeleteButton type="button" onClick={onRemoveImage}>
                            <svg width="40" height="32" viewBox="0 0 40 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path
                                    fillRule="evenodd"
                                    clipRule="evenodd"
                                    d="M17.4268 5.96289C16.5983 5.96289 15.9268 6.63446 15.9268 7.46289V7.96289H12.4268C11.5983 7.96289 10.9268 8.63446 10.9268 9.46289V10.4629C10.9268 11.2913 11.5983 11.9629 12.4268 11.9629H12.9268V24.4629C12.9268 25.2913 13.5983 25.9629 14.4268 25.9629H25.4268C26.2552 25.9629 26.9268 25.2913 26.9268 24.4629V11.9629H27.4268C28.2552 11.9629 28.9268 11.2913 28.9268 10.4629V9.46289C28.9268 8.63446 28.2552 7.96289 27.4268 7.96289H23.9268V7.46289C23.9268 6.63446 23.2552 5.96289 22.4268 5.96289H17.4268ZM22.9268 7.96289V7.46289C22.9268 7.18675 22.7029 6.96289 22.4268 6.96289H17.4268C17.1506 6.96289 16.9268 7.18675 16.9268 7.46289V7.96289H22.9268ZM15.9268 8.96289H12.4268C12.1506 8.96289 11.9268 9.18675 11.9268 9.46289V10.4629C11.9268 10.739 12.1506 10.9629 12.4268 10.9629H12.9268H13.4268H26.4268H26.9268H27.4268C27.7029 10.9629 27.9268 10.739 27.9268 10.4629V9.46289C27.9268 9.18675 27.7029 8.96289 27.4268 8.96289H23.9268H23.4268H16.4268H15.9268ZM13.9268 11.9629H25.9268V24.4629C25.9268 24.739 25.7029 24.9629 25.4268 24.9629H14.4268C14.1506 24.9629 13.9268 24.739 13.9268 24.4629V11.9629Z"
                                    fill="#E7E7E7"
                                />
                            </svg>
                        </ImageDeleteButton>
                    )}
                </ImageBlockPlaceholder>
            </ImageBlock>

            <FieldsColumn>
                <FlexBox style={{ position: "relative" }} direction="column" width="100%">
                    <CommonInput
                        type={INPUT_TYPES.text}
                        value={data?.nameEng}
                        onChange={(ev: any) => onInputChange(ev, 'nameEng')}
                        orientation="column"
                        label={t("tags_modal.tag_name_eng")}
                        placeholder={t("tags_modal.tag_input_placeholder")}
                    />
                </FlexBox>

                <FlexBox style={{ position: "relative" }} direction="column" width="100%">
                    <CommonInput
                        type={INPUT_TYPES.text}
                        value={data?.nameRu}
                        onChange={(ev: any) => onInputChange(ev, 'nameRu')}
                        orientation="column"
                        label={t(getTagsModalLocalNameLabelKey())}
                        placeholder={t("tags_modal.tag_input_placeholder")}
                    />
                </FlexBox>

                {/* <FlexBox style={{ position: 'relative' }} direction="column" width="100%">
                    <CommonInput
                        type={INPUT_TYPES.search}
                        value={data?.coordinate}
                        onChange={(ev: any) => onInputChange(ev, 'coordinate')}
                        orientation="column"
                        label={t("tags_modal.tag_geo_places")}
                        placeholder={t("tags_modal.geo_places_search")}
                        infoText={t('tags_modal.for_tag_point')}
                        onWhiteBG
                    />
                    {!!pointResults?.length && (
                        <PointResultContainer>
                            {pointResults}
                        </PointResultContainer>
                    )}
                </FlexBox> */}

                <FlexBox direction="column" width="100%">
                    <ProjectAuthorsEdit
                        label={t("tags_modal.tag_user")}
                        infoText={t('tags_modal.for_tag_artists')}
                        value={data?.author || []}
                        onChange={onProjectAuthorsChange}
                        orientation="column"
                        persist
                    />
                </FlexBox>

                <FlexBox direction="column" width="100%">
                    <CommonInput
                        type={INPUT_TYPES.textarea}
                        value={data?.description}
                        onChange={(ev: any) => onInputChange(ev, 'description')}
                        orientation="column"
                        label={t('tags_modal.description_label')}
                        placeholder={t('tags_modal.description_placeholder')}
                        onWhiteBG
                    />
                </FlexBox>
            </FieldsColumn>
        </ContentContainer>
    );

    const header = (
        <PersonalizedHeader>
            <Title>{isEditMode ? t("tags_modal.edit_tag_title") : t("tags_modal.create_tag_title")}</Title>
            {isModal ? (
                <BrgButton
                    variant={ButtonVariant.Filled}
                    onClick={onSubmit}
                    disabled={disable}
                >
                    {t("tags_modal.save_title")}
                </BrgButton>
            ) : (
                <SubmitButton 
                    onClick={onSubmit} 
                    disabled={disable}
                >
                    {t("tags_modal.save_title")}
                </SubmitButton>
            )}
        </PersonalizedHeader>
    );

    if (isModal) {
        return (
           <PersonalizedTagModalWrapper>
            {header}
            {formContent}
           </PersonalizedTagModalWrapper>
        )
    }

    return (
        <PersonalizedTagWrapper>
            {header}
            {formContent}
        </PersonalizedTagWrapper>
    )
}
